import { useAuth } from '@/hooks/useAuth';
import { CreateStreamMessagePayload } from '@/interface/chat';
import { logger } from '@/util/logger';
import { getFastApiUrl } from '@/util/fastApiUrl';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import axiosInstance from '../api/axiosConfig';

export function useApiQuery<T>(
  key: string[],
  url: string,
  options?: Partial<UseQueryOptions<T, AxiosError>>,
  isProd?: boolean,
  params?: Record<string, string | number>,
) {
  const { authState } = useAuth();

  return useQuery<T, AxiosError>({
    queryKey: key,
    queryFn: async () => {
      try {
        logger.info({
          message: `API Request: GET ${url}`,
          context: { url, method: 'GET', isProd },
        });

        const response = await axiosInstance.get(url, {
          baseURL: isProd
            ? process.env.REACT_APP_API_URL
            : process.env.REACT_APP_OCHESTRATOR_API_URL,
          headers: authState?.idToken?.idToken
            ? {
                Authorization: `Bearer ${authState?.idToken?.idToken}`,
              }
            : undefined,
          params,
        });

        logger.info({
          message: `API Response: GET ${url}`,
          context: { url, method: 'GET', status: response.status, isProd },
        });

        return response.data;
      } catch (error) {
        logger.error({
          message: `API Error: GET ${url}`,
          error,
          context: {
            url,
            method: 'GET',
            status: (error as AxiosError)?.response?.status,
          },
        });
        throw error;
      }
    },
    ...options,
  });
}

export function useApiMutationWithBaseUrl<T, S>({
  baseURL,
  url,
  method = 'post',
  options,
  endpoint = 'chat',
}: {
  baseURL: string;
  url: string;
  method?: 'post' | 'put' | 'patch' | 'delete';
  options?: UseMutationOptions<T, AxiosError, S>;
  endpoint?: 'chat' | 'prod' | 'staff';
}) {
  const { authState } = useAuth();

  return useMutation<T, AxiosError, S>({
    mutationFn: async (data: S) => {
      try {
        const jsonData = JSON.stringify(data);

        logger.info({
          message: `API Request: ${method.toUpperCase()} ${url}`,
          context: {
            url,
            method: method.toUpperCase(),
            data,
            endpoint,
          },
        });

        const requestConfig = {
          baseURL,
          headers: {
            'Content-Type': 'application/json',
            ...(authState?.idToken?.idToken && {
              Authorization: `Bearer ${authState.idToken.idToken}`,
            }),
          },
        };

        const response = await axiosInstance.request<T>({
          method,
          url,
          data: jsonData,
          ...requestConfig,
        });

        logger.info({
          message: `API Response: ${method.toUpperCase()} ${url}`,
          context: {
            url,
            method: method.toUpperCase(),
            status: response.status,
            endpoint,
          },
        });

        return response.data;
      } catch (error) {
        logger.error({
          message: `API Error: ${method.toUpperCase()} ${url}`,
          error,
          context: {
            url,
            method: method.toUpperCase(),
            status: (error as AxiosError)?.response?.status,
            data,
          },
        });
        throw error;
      }
    },
    ...options,
  });
}

export function useApiMutation<T, S>({
  url,
  method = 'post',
  options,
  endpoint = 'chat',
  useParams = false,
}: {
  url: string;
  method?: 'post' | 'put' | 'patch' | 'delete';
  options?: UseMutationOptions<T, AxiosError, S>;
  endpoint?: 'chat' | 'prod' | 'staff';
  useParams?: boolean;
}) {
  const { authState } = useAuth();

  return useMutation<T, AxiosError, S>({
    mutationFn: async (data: S) => {
      try {
        const requestUrl = useParams
          ? `${url}?${new URLSearchParams(data as any).toString()}`
          : url;

        logger.info({
          message: `API Request: ${method.toUpperCase()} ${requestUrl}`,
          context: {
            url: requestUrl,
            method: method.toUpperCase(),
            data,
            endpoint,
          },
        });

        const requestConfig = {
          baseURL:
            endpoint === 'prod'
              ? process.env.REACT_APP_API_URL
              : endpoint === 'staff'
                ? process.env.REACT_APP_STAFF_API_URL
                : process.env.REACT_APP_ORCHESTRATOR_API_URL,
          headers: {
            ...(authState?.idToken?.idToken && {
              Authorization: `Bearer ${authState.idToken.idToken}`,
            }),
          },
        };
        const axiosMethod = axiosInstance[method] as (
          url: string,
          data?: any,
          config?: any,
        ) => Promise<any>;

        let response;
        if (useParams) {
          response = await axiosInstance[method](requestUrl, requestConfig);
        } else {
          response = await axiosMethod(url, data, requestConfig);
        }

        logger.info({
          message: `API Response: ${method.toUpperCase()} ${requestUrl}`,
          context: {
            url: requestUrl,
            method: method.toUpperCase(),
            status: response.status,
            endpoint,
          },
        });

        return response.data;
      } catch (error) {
        logger.error({
          message: `API Error: ${method.toUpperCase()} ${url}`,
          error,
          context: {
            url,
            method: method.toUpperCase(),
            status: (error as AxiosError)?.response?.status,
            data,
          },
        });
        throw error;
      }
    },
    ...options,
  });
}

export function useApiStream<S = CreateStreamMessagePayload>(
  url: string,
  isProd?: boolean,
) {
  const { authState } = useAuth();

  const streamData = async (
    data: S,
    onChunk: (chunk: string) => void,
    onComplete: (
      messageId?: string,
      deepLink?: string,
      parsedResponse?: any,
    ) => void,
    onError: (error: Error) => void,
    signal?: AbortSignal,
  ) => {
    let messageId: string | null = null;
    let deepLink: string | undefined;
    const DELIMITER = '#%&response&%#';
    let seenDelimiter = false;
    let textBuffer = '';
    let jsonBuffer = '';

    try {
      logger.info({
        message: `API Stream Request: POST ${url}`,
        context: { url, method: 'POST', data, isProd },
      });

      if (signal?.aborted) throw new Error('Stream was aborted');

      const response = await fetch(getFastApiUrl() + url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authState?.idToken?.idToken
            ? { Authorization: `Bearer ${authState.idToken.idToken}` }
            : {}),
        },
        body: JSON.stringify(data),
        signal,
      });

      messageId = response.headers.get('X-Message-Id');

      if (!response.ok) {
        const text = await response.text();
        let msg = `Stream request failed with status ${response.status}`;
        try {
          const err = JSON.parse(text);
          msg = err.message || err.error || msg;
        } catch {}
        throw new Error(msg);
      }

      if (!response.body) throw new Error('Response body is null');

      logger.info({
        message: `API Stream Connected: POST ${url}`,
        context: {
          url,
          method: 'POST',
          status: response.status,
          isProd,
          messageId,
        },
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Read until the stream ends
      while (true) {
        if (signal?.aborted) {
          reader.cancel();
          throw new Error('Stream was aborted');
        }

        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        textBuffer += chunk;

        if (!seenDelimiter) {
          const idx = textBuffer.indexOf(DELIMITER);
          if (idx !== -1) {
            // emit everything before the delimiter
            const before = textBuffer.slice(0, idx);
            if (before) onChunk(before);

            seenDelimiter = true;
            // anything after the delimiter goes into jsonBuffer
            jsonBuffer += textBuffer.slice(idx + DELIMITER.length);
            textBuffer = '';
          } else {
            // no delimiter yet → stream out all text so far
            onChunk(textBuffer);
            textBuffer = '';
          }
        } else {
          // already saw delimiter → keep collecting JSON
          jsonBuffer += textBuffer;
          textBuffer = '';
        }
      }

      // Once the stream has ended, try to parse the JSON
      let parsedResponse = null;
      if (seenDelimiter && jsonBuffer) {
        try {
          const parsed = JSON.parse(jsonBuffer);
          const link =
            typeof parsed.deeplink === 'string'
              ? parsed.deeplink
              : typeof parsed.deepLink === 'string'
                ? parsed.deepLink
                : undefined;
          deepLink = link;

          // Extract all the required fields for the new payload
          parsedResponse = {
            userId: parsed.userId || (data as any).uuid || '',
            conversationId:
              parsed.conversationId || (data as any).conversationId || '',
            messageContent:
              parsed.messageContent || (data as any).question || '',
            messageType: parsed.messageType || 'text',
            deviceType: parsed.deviceType || 'desktop',
            isUpdatedConversation: !(data as any).is_first_message,
            username: parsed.username || (data as any).username || '',
            chatType: parsed.chatType || 'Text Mode',
            is_first_message: (data as any).is_first_message || false,
            answer: parsed.answer || '',
            conversation_summary: parsed.conversation_summary || '',
            deeplink: parsed.deeplink || parsed.deepLink || '',
            sql_query: parsed.sql_query || '',
            table_used: parsed.table_used || '',
            sql_success: parsed.sql_success || false,
            rewritten_query: parsed.rewritten_query || null,
            sql_result: parsed.sql_result || null,
            documents: Array.isArray(parsed.documents)
              ? parsed.documents
              : undefined,
            function_timings: {
              generate_conversation_summary:
                parsed.function_timings?.generate_conversation_summary || 0,
              similarity_search:
                parsed.function_timings?.similarity_search || 0,
              generate_sql_query:
                parsed.function_timings?.generate_sql_query || 0,
              execute_sql_query:
                parsed.function_timings?.execute_sql_query || 0,
              generate_final_answer:
                parsed.function_timings?.generate_final_answer || 0,
              answer_directly_with_rag:
                parsed.function_timings?.answer_directly_with_rag || 0,
            },
            is_graph: parsed?.is_graph || false,
          };
        } catch (e) {
          logger.error({
            message: 'Failed to parse JSON tail from stream',
            error: e,
            context: { raw: jsonBuffer },
          });
        }
      }

      onComplete(messageId || undefined, deepLink, parsedResponse);
    } catch (error) {
      // Distinguish aborts from real errors
      if (
        (error as Error).name === 'AbortError' ||
        (error as Error).message === 'Stream was aborted'
      ) {
        logger.info({
          message: `API Stream Aborted: POST ${url}`,
          context: { url, method: 'POST', data },
        });
        onError(new Error('Stream was aborted'));
      } else {
        logger.error({
          message: `API Stream Error: POST ${url}`,
          error,
          context: { url, method: 'POST', data },
        });
        onError(error as Error);
      }
      // Still fire onComplete so callers know we're done
      onComplete(messageId || undefined, undefined, null);
    }

    return { messageId };
  };

  return { streamData };
}
